/**
 * The rules' versions and their workflow, in the 规则 tab's header: pick the version to show, open a
 * draft, submit it, publish or send back a version in review (with a reason), record a published
 * version's engine implementation, discard a draft.
 *
 *   draft --submit--> in_review --publish--> published --(next publish)--> archived
 *                         \--reject--> draft
 *
 * These are versions of the rules document only (the 版本 tab is reserved for versioning the whole
 * recommendation configuration). The service enforces every transition — one open version at a time,
 * the author cannot publish their own version — and the controls only hide what it would refuse.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/AuthContext';
import {
  CreateRuleDraftCommand,
  DeleteRuleDraftCommand,
  getCpaClient,
  PublishRuleVersionCommand,
  RejectRuleVersionCommand,
  type RuleVersionSummary,
  SubmitRuleVersionCommand,
  UpdateRuleVersionEngineSyncCommand,
} from '../../client/CpaClient';
import { useRuleVersions } from '../../state/useRuleVersions';
import { actionsFor, openVersion, type VersionAction, workflowErrorOf } from '../../utils/ruleVersions';

/** Runs one workflow action on a version. `input` is the reject reason or the engine reference. */
function runAction(action: VersionAction, v: RuleVersionSummary, input: string) {
  const client = getCpaClient();
  const version = v.version!;
  const expectedUpdatedAt = v.updatedAt;
  switch (action) {
    case 'submit':
      return client.send(new SubmitRuleVersionCommand({ version, expectedUpdatedAt }));
    case 'publish':
      return client.send(new PublishRuleVersionCommand({ version, expectedUpdatedAt }));
    case 'reject':
      return client.send(new RejectRuleVersionCommand({ version, reason: input.trim(), expectedUpdatedAt }));
    case 'implemented':
      return client.send(new UpdateRuleVersionEngineSyncCommand({ version, reference: input.trim() }));
    case 'discard':
      return client.send(new DeleteRuleDraftCommand({ version }));
  }
}

const NEEDS_TEXT: Partial<Record<VersionAction, true>> = { reject: true, implemented: true };

export default function VersionControls({
  shown,
  onShow,
  onChanged,
}: {
  /** The version the page shows. */
  shown: number;
  /** Show another version; undefined = the one in force. */
  onShow: (version: number | undefined) => void;
  /** A workflow action changed the shown version. */
  onChanged: () => void;
}) {
  const { t } = useTranslation('cpa');
  // Keys picked at runtime (status, action, error); the static ones keep their checked types.
  const tx = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
  const { uid } = useAuth();
  const { state, reload } = useRuleVersions();
  const [pending, setPending] = useState<VersionAction | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ key: string; detail?: string } | null>(null);
  // Idempotency token for the next "new draft": a retried click reuses it (and gets the same draft); a
  // successful create replaces it, so the next draft is a new one.
  const [clientToken, setClientToken] = useState(() => crypto.randomUUID());

  const versions = state.kind === 'ready' ? state.versions : [];
  const current = versions.find((v) => v.version === shown);
  const open = openVersion(versions);
  const actions = current ? actionsFor(current, uid) : [];
  const label = (v: RuleVersionSummary) => `v${v.version} · ${tx(`versionStatus_${v.status}`)}`;

  const closeDialog = () => {
    setPending(null);
    setError(null);
  };

  const createDraft = () => {
    setBusy(true);
    setError(null);
    getCpaClient()
      .send(new CreateRuleDraftCommand({ clientToken }))
      .then((out) => {
        setClientToken(crypto.randomUUID());
        reload();
        onShow(out.rules!.version);
      })
      .catch((e: unknown) => setError(workflowErrorOf(e)))
      .finally(() => setBusy(false));
  };

  const confirm = () => {
    if (!pending || !current) return;
    setBusy(true);
    setError(null);
    runAction(pending, current, text)
      .then(() => {
        const discarded = pending === 'discard';
        setPending(null);
        setText('');
        reload();
        if (discarded) onShow(undefined);
        else onChanged();
      })
      .catch((e: unknown) => setError(workflowErrorOf(e)))
      .finally(() => setBusy(false));
  };

  return (
    <SpaceBetween size="xs">
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <div className="cpa-rules-version-select">
          <Select
            ariaLabel={t('rulesVersionPick')}
            statusType={state.kind === 'loading' ? 'loading' : 'finished'}
            selectedOption={current ? { value: String(current.version), label: label(current) } : null}
            placeholder={`v${shown}`}
            options={versions.map((v) => ({ value: String(v.version), label: label(v) }))}
            onChange={({ detail }) => onShow(Number(detail.selectedOption.value))}
          />
        </div>
        {actions.map((a) => (
          <Button
            key={a}
            variant={a === 'publish' ? 'primary' : 'normal'}
            onClick={() => {
              setError(null);
              setText('');
              setPending(a);
            }}
          >
            {tx(`versionAction_${a}`)}
          </Button>
        ))}
        {state.kind === 'ready' && !open && (
          <Button iconName="add-plus" loading={busy && !pending} onClick={createDraft}>
            {t('versionsNewDraft')}
          </Button>
        )}
        {open && open.version !== shown && (
          <Button variant="link" onClick={() => onShow(open.version)}>
            {t('rulesGoToOpen', { version: open.version })}
          </Button>
        )}
      </SpaceBetween>
      {error && !pending && (
        <Alert type="error" dismissible onDismiss={() => setError(null)} header={tx(error.key)}>
          {error.detail}
        </Alert>
      )}

      {pending && current && (
        <Modal
          visible
          onDismiss={closeDialog}
          header={`${tx(`versionAction_${pending}`)} · v${current.version}`}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={closeDialog} disabled={busy}>
                  {t('cancel')}
                </Button>
                <Button
                  variant="primary"
                  loading={busy}
                  disabled={!!NEEDS_TEXT[pending] && !text.trim()}
                  onClick={confirm}
                >
                  {t('versionsConfirm')}
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box>{tx(`versionConfirm_${pending}`)}</Box>
            {pending === 'reject' && (
              <FormField label={t('versionsReason')}>
                <Textarea value={text} onChange={({ detail }) => setText(detail.value)} />
              </FormField>
            )}
            {pending === 'implemented' && (
              <FormField label={t('versionsReference')} constraintText={t('versionsReferenceHint')}>
                <Input value={text} onChange={({ detail }) => setText(detail.value)} />
              </FormField>
            )}
            {error && (
              <Alert type="error" header={tx(error.key)}>
                {error.detail}
              </Alert>
            )}
          </SpaceBetween>
        </Modal>
      )}
    </SpaceBetween>
  );
}
